import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ApiClientError } from '@/lib/api/client'
import { fetchLandingCampaign } from '@/lib/landing'
import { initializeMetaPixel } from '@/lib/meta-pixel'
import {
  landingCheckDonorPhoneAccess,
  landingDonorForgotPasswordByPhone,
  landingFetchDonorMe,
  landingDonorLoginByPhone,
  landingDonorRegister,
  landingDonorResetPassword,
  type LandingDonorAccessStatus,
} from '@/lib/landing-auth'
import {
  establishLandingDonorSession,
  writeLandingDonorToken,
} from '@/lib/landing-donor-session'
import {
  clearPaymentAutoAuthCheckoutProof,
  landingExchangePaymentAutoAuth,
  readPaymentAutoAuthCheckoutProof,
  stripPaymentAutoAuthParamsFromUrl,
} from '@/lib/landing-payment-auto-auth'
import { parsePasswordResetQueryParams } from '@/lib/password-reset-url'
import { useLandingCampaignWidget } from '@/hooks/useLandingCampaignWidget'
import { useLandingCampaignPageView } from '@/hooks/useLandingCampaignPageView'
import { useLandingDonorSso } from '@/hooks/useLandingDonorSso'
import { isCampaignOpenForSales } from '@/lib/campaign-sales'
import { resolveCampaignHomeHref } from '@/lib/campaign-view-page-url'
import { LandingSalesClosedNotice } from '@/components/Landing/LandingSalesClosedNotice'
import type {
  LandingCampaign,
  LandingCampaignResponse,
  LandingInstitution,
} from '@/types/landing'
import { ParticipantThemeStyle } from '@/components/ParticipantThemeStyle'
import { getInstitutionBrandImageUrl } from '@/lib/profile-media'
import {
  buildLandingInstitutionName,
  resolveLandingAccountBackdropUrl,
  resolveLandingAccountPrizeLine,
  resolveLandingAccountTitle,
} from '@/lib/landing-account-visual'
import { LandingAccountForm } from './LandingAccountForm'
import {
  isValidAccountEmail,
  maskPhone,
  normalizeCpfDigits,
  normalizePhoneDigits,
} from './landing-account-format'
import type { AccountFlowStep } from './landing-account-copy'
import styles from './LandingAccountPage.module.css'

function mapAccessStatusToStep(status: LandingDonorAccessStatus): AccountFlowStep {
  switch (status) {
    case 'password_required':
      return 'password'
    case 'password_creation_required':
      return 'forgot-password'
    case 'registration_required':
    default:
      return 'register'
  }
}

const ACCOUNT_PHONE_STORAGE_KEY = 'legalprime:accountPhone:global'

function readRememberedAccountPhone(slug: string | undefined): string {
  if (typeof window === 'undefined') return ''

  try {
    const stored =
      window.localStorage.getItem(ACCOUNT_PHONE_STORAGE_KEY) ??
      (slug ? window.localStorage.getItem(`legalprime:accountPhone:${slug}`) : null) ??
      ''
    return /^\d{11}$/.test(stored) ? maskPhone(stored) : ''
  } catch {
    return ''
  }
}

function rememberAccountPhone(value: string): void {
  const digits = normalizePhoneDigits(value)
  if (digits.length !== 11 || typeof window === 'undefined') return

  try {
    window.localStorage.setItem(ACCOUNT_PHONE_STORAGE_KEY, digits)
  } catch {
    // O login continua mesmo quando o navegador bloqueia o armazenamento local.
  }
}

export function LandingAccountPage() {
  const navigate = useNavigate()
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const phonePrefill = maskPhone(searchParams.get('phone') ?? '')
  const [landingData, setLandingData] = useState<LandingCampaignResponse | null>(null)
  const [flowStep, setFlowStep] = useState<AccountFlowStep>('phone')
  const [isLoading, setIsLoading] = useState(true)
  const [institutionId, setInstitutionId] = useState<number | null>(null)
  const [authError, setAuthError] = useState<string | null>(null)
  const [authSuccess, setAuthSuccess] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [resetToken, setResetToken] = useState('')
  const [resetEmail, setResetEmail] = useState('')
  const lastAppliedResetParamsKey = useRef<string | null>(null)
  const [phone, setPhone] = useState(
    () => phonePrefill || readRememberedAccountPhone(slug)
  )
  const [loginPassword, setLoginPassword] = useState('')
  const [accountPassword, setAccountPassword] = useState('')
  const [accountPasswordConfirm, setAccountPasswordConfirm] = useState('')
  const [registerName, setRegisterName] = useState('')
  const [registerCpf, setRegisterCpf] = useState('')
  const [registerEmail, setRegisterEmail] = useState('')
  const [isAutoAuthPending, setIsAutoAuthPending] = useState(false)
  const isEmbeddedWindow = window.top !== window
  const paymentAutoAuthAttempted = useRef(false)

  const paymentAutoAuthToken = searchParams.get('auto_auth_token')?.trim() ?? ''
  const paymentAutoAuthPaymentId = useMemo(() => {
    const raw = searchParams.get('payment_id')?.trim() ?? ''
    const parsed = Number(raw)
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null
  }, [searchParams])
  const shouldAttemptPaymentAutoAuth =
    searchParams.get('return')?.trim() === 'account' &&
    paymentAutoAuthToken !== '' &&
    paymentAutoAuthPaymentId !== null
  const { token: passwordResetToken, email: passwordResetEmail } =
    parsePasswordResetQueryParams(searchParams)
  const resetDonorId = Number(searchParams.get('donor_id'))
  const hasResetDonorId = Number.isSafeInteger(resetDonorId) && resetDonorId > 0
  const hasPasswordResetLink = Boolean(
    passwordResetToken && (passwordResetEmail || hasResetDonorId)
  )

  useEffect(() => {
    if (!phonePrefill) return
    setPhone((current) => (current.trim() === '' ? phonePrefill : current))
  }, [phonePrefill])

  const quantity = useMemo(() => {
    const value = Number(searchParams.get('quantity') || '')
    return Number.isFinite(value) && value > 0 ? String(Math.floor(value)) : null
  }, [searchParams])

  const redirectToLanding = useCallback(() => {
    if (!slug) {
      navigate('/', { replace: true })
      return
    }

    const params = new URLSearchParams()
    if (quantity) params.set('quantity', quantity)
    const af = searchParams.get('af')?.trim()
    if (af) params.set('af', af)

    const nextPath = `/campanha/${slug}/perfil`
    const suffix = params.toString() ? `?${params.toString()}` : ''
    navigate(`${nextPath}${suffix}`, { replace: true })
  }, [navigate, quantity, searchParams, slug])

  const onSsoAuthenticated = useCallback(
    (path: string) => navigate(path, { replace: true }),
    [navigate]
  )
  const {
    pending: isSsoPending,
    isCallback: isSsoCallback,
    start: startSso,
    finishSignIn: finishSsoSignIn,
    confirmation: ssoConfirmation,
    confirmSignIn: confirmSsoSignIn,
    cancelSignIn: cancelSsoSignIn,
  } = useLandingDonorSso({
    slug,
    searchParams,
    onAuthenticated: onSsoAuthenticated,
    onError: setAuthError,
  })
  const completeSignIn = useCallback(async () => {
    if (!(await finishSsoSignIn())) redirectToLanding()
  }, [finishSsoSignIn, redirectToLanding])

  useEffect(() => {
    if (
      !shouldAttemptPaymentAutoAuth ||
      paymentAutoAuthAttempted.current ||
      !institutionId
    ) {
      return
    }

    paymentAutoAuthAttempted.current = true
    setIsAutoAuthPending(true)
    setAuthError(null)

    const checkoutProof =
      readPaymentAutoAuthCheckoutProof(paymentAutoAuthPaymentId)?.checkoutToken ??
      searchParams.get('checkout_token')?.trim() ??
      ''

    void landingExchangePaymentAutoAuth({
      institution_id: institutionId,
      payment_id: paymentAutoAuthPaymentId,
      auto_auth_token: paymentAutoAuthToken,
      ...(checkoutProof ? { checkout_token: checkoutProof } : {}),
    })
      .then(async () => {
        const sessionOk = await establishLandingDonorSession()
        if (!sessionOk) {
          setAuthError('Não foi possível abrir sua sessão. Faça login para continuar.')
          return
        }
        clearPaymentAutoAuthCheckoutProof(paymentAutoAuthPaymentId)
        stripPaymentAutoAuthParamsFromUrl()
        await completeSignIn()
      })
      .catch(() => {
        clearPaymentAutoAuthCheckoutProof(paymentAutoAuthPaymentId)
        stripPaymentAutoAuthParamsFromUrl()
        setAuthError(null)
      })
      .finally(() => {
        setIsAutoAuthPending(false)
      })
  }, [
    institutionId,
    paymentAutoAuthPaymentId,
    paymentAutoAuthToken,
    completeSignIn,
    searchParams,
    shouldAttemptPaymentAutoAuth,
  ])

  useEffect(() => {
    if (!slug) {
      setIsLoading(false)
      return
    }

    let mounted = true
    const loadCampaign = async () => {
      setIsLoading(true)
      setAuthError(null)
      try {
        const response = await fetchLandingCampaign(slug)
        if (!mounted) return
        setLandingData(response)
        const landingPayload = response.data
        if (!landingPayload) {
          setAuthError('Campanha temporariamente indisponível.')
          return
        }
        setInstitutionId(landingPayload.institution.id)

        if (!shouldAttemptPaymentAutoAuth && !hasPasswordResetLink && !isSsoCallback) {
          try {
            await landingFetchDonorMe('cookie', landingPayload.campaign.id)
            if (!mounted) return
            writeLandingDonorToken('cookie')
            redirectToLanding()
            return
          } catch (sessionError) {
            if (sessionError instanceof ApiClientError && sessionError.status === 401) {
              writeLandingDonorToken(null)
              if (searchParams.get('signed_out') !== '1')
                await startSso(landingPayload.campaign.id)
            }
          }
        }
      } catch (err) {
        if (!mounted) return
        setAuthError(
          err instanceof ApiClientError
            ? err.message
            : 'Não foi possível carregar os dados da campanha.'
        )
      } finally {
        if (mounted) setIsLoading(false)
      }
    }

    void loadCampaign()
    return () => {
      mounted = false
    }
  }, [
    hasPasswordResetLink,
    isSsoCallback,
    redirectToLanding,
    searchParams,
    shouldAttemptPaymentAutoAuth,
    slug,
    startSso,
  ])

  useEffect(() => {
    const { token, email } = parsePasswordResetQueryParams(searchParams)
    if (!token || (!email && !hasResetDonorId)) {
      lastAppliedResetParamsKey.current = null
      return
    }

    const resetParamsKey = `${token}|${email}|${hasResetDonorId ? resetDonorId : ''}`
    if (lastAppliedResetParamsKey.current === resetParamsKey) {
      return
    }

    lastAppliedResetParamsKey.current = resetParamsKey
    setResetToken(token)
    setResetEmail(email)
    setFlowStep('reset-password')
    setAuthError(null)
    setAuthSuccess(null)
  }, [hasResetDonorId, resetDonorId, searchParams])

  const resetPasswordFields = useCallback(() => {
    setLoginPassword('')
    setAccountPassword('')
    setAccountPasswordConfirm('')
  }, [])

  const handleCheckPhone = useCallback(async () => {
    if (landingData?.data?.campaign.id && (await startSso(landingData.data.campaign.id)))
      return
    const normalizedPhone = normalizePhoneDigits(phone)
    if (normalizedPhone.length < 11) {
      setAuthError('Informe um telefone válido com DDD.')
      return
    }

    setAuthError(null)
    setIsSubmitting(true)
    try {
      const response = await landingCheckDonorPhoneAccess({
        ...(institutionId ? { institution_id: institutionId } : {}),
        phone: normalizedPhone,
      })

      setPhone(maskPhone(response.phone || normalizedPhone))
      setFlowStep(
        response.has_account && !response.has_password
          ? 'forgot-password'
          : mapAccessStatusToStep(response.status)
      )
      resetPasswordFields()
    } catch (err) {
      setAuthError(
        err instanceof ApiClientError
          ? err.message
          : 'Não foi possível verificar seu telefone agora.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [institutionId, landingData, phone, resetPasswordFields, startSso])

  const handleLogin = useCallback(async () => {
    setAuthError(null)
    setIsSubmitting(true)
    try {
      await landingDonorLoginByPhone(
        {
          ...(institutionId ? { institution_id: institutionId } : {}),
          phone: normalizePhoneDigits(phone),
          password: loginPassword,
        },
        true
      )
      const sessionOk = await establishLandingDonorSession()
      if (!sessionOk) {
        setAuthError('Não foi possível abrir sua sessão. Tente novamente.')
        return
      }
      rememberAccountPhone(phone)
      await completeSignIn()
    } catch (err) {
      setAuthError(
        err instanceof ApiClientError
          ? err.message
          : 'Não foi possível entrar. Verifique seus dados.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [completeSignIn, institutionId, loginPassword, phone])

  const handleRegister = useCallback(async () => {
    const cpfDigits = normalizeCpfDigits(registerCpf)
    const phoneDigits = normalizePhoneDigits(phone)
    const normalizedRegisterEmail = registerEmail.trim().toLowerCase()

    if (!registerName.trim()) {
      setAuthError('Informe seu nome completo.')
      return
    }
    if (!normalizedRegisterEmail) {
      setAuthError('Informe seu e-mail para concluir o cadastro.')
      return
    }
    if (!isValidAccountEmail(normalizedRegisterEmail)) {
      setAuthError('Informe um e-mail válido.')
      return
    }
    if (accountPassword.length < 8) {
      setAuthError('A senha deve ter no mínimo 8 caracteres.')
      return
    }
    if (accountPassword !== accountPasswordConfirm) {
      setAuthError('As senhas não conferem.')
      return
    }
    if (cpfDigits.length !== 11) {
      setAuthError('Informe um CPF válido com 11 números.')
      return
    }
    if (phoneDigits.length < 11) {
      setAuthError('Informe um telefone válido com DDD.')
      return
    }

    setAuthError(null)
    setIsSubmitting(true)
    try {
      await landingDonorRegister(
        {
          ...(institutionId ? { institution_id: institutionId } : {}),
          person_type: 'pf',
          full_name: registerName.trim(),
          document_cpf: cpfDigits,
          email: normalizedRegisterEmail,
          phone: phoneDigits,
          password: accountPassword,
        },
        true
      )
      const sessionOk = await establishLandingDonorSession()
      if (!sessionOk) {
        setAuthError('Não foi possível abrir sua sessão. Tente novamente.')
        return
      }
      rememberAccountPhone(phone)
      await completeSignIn()
    } catch (err) {
      setAuthError(
        err instanceof ApiClientError
          ? err.message
          : 'Não foi possível concluir o cadastro.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [
    accountPassword,
    accountPasswordConfirm,
    institutionId,
    phone,
    completeSignIn,
    registerCpf,
    registerEmail,
    registerName,
  ])

  const clearResetQueryParams = useCallback(() => {
    const params = new URLSearchParams()
    const returnValue = searchParams.get('return')?.trim()
    const quantityValue = searchParams.get('quantity')?.trim()
    const afValue = searchParams.get('af')?.trim()
    const phoneValue = searchParams.get('phone')?.trim()

    if (returnValue) params.set('return', returnValue)
    if (quantityValue) params.set('quantity', quantityValue)
    if (afValue) params.set('af', afValue)
    if (phoneValue) params.set('phone', phoneValue)

    lastAppliedResetParamsKey.current = null
    const suffix = params.toString() ? `?${params.toString()}` : ''
    navigate(`${slug ? `/campanha/${slug}/conta` : '/conta'}${suffix}`, { replace: true })
  }, [navigate, searchParams, slug])

  const handleForgotPassword = useCallback(async () => {
    const phoneDigits = normalizePhoneDigits(phone)
    if (phoneDigits.length < 11) {
      setAuthError('Informe um telefone válido com DDD.')
      return
    }

    setAuthError(null)
    setAuthSuccess(null)
    setIsSubmitting(true)

    try {
      const message = await landingDonorForgotPasswordByPhone({
        phone: phoneDigits,
      })
      setAuthSuccess(
        message ||
          'Se houver uma conta cadastrada, enviaremos um link para redefinir a senha por e-mail e WhatsApp, conforme os contatos disponíveis no cadastro.'
      )
      setFlowStep('forgot-password-sent')
    } catch (err) {
      setAuthError(
        err instanceof ApiClientError
          ? err.message
          : 'Não foi possível enviar o link de recuperação agora.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [phone])

  const handleResetPassword = useCallback(async () => {
    if (!resetToken.trim() || (!resetEmail.trim() && !hasResetDonorId)) {
      setAuthError('Link de recuperação inválido ou incompleto.')
      return
    }

    if (accountPassword.length < 8) {
      setAuthError('A senha deve ter no mínimo 8 caracteres.')
      return
    }

    if (accountPassword !== accountPasswordConfirm) {
      setAuthError('As senhas não conferem.')
      return
    }

    setAuthError(null)
    setAuthSuccess(null)
    setIsSubmitting(true)

    try {
      const message = await landingDonorResetPassword({
        ...(hasResetDonorId ? { donor_id: resetDonorId } : {}),
        ...(resetEmail ? { email: resetEmail } : {}),
        token: resetToken,
        password: accountPassword,
        password_confirmation: accountPasswordConfirm,
      })

      resetPasswordFields()
      setResetToken('')
      setResetEmail('')
      clearResetQueryParams()
      setFlowStep('phone')
      setAuthSuccess(
        message ||
          'Senha redefinida com sucesso. Informe seu telefone e a nova senha para entrar.'
      )
    } catch (err) {
      setAuthError(
        err instanceof ApiClientError
          ? err.message
          : 'Não foi possível redefinir sua senha. Solicite um novo link.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [
    accountPassword,
    accountPasswordConfirm,
    clearResetQueryParams,
    resetEmail,
    resetDonorId,
    hasResetDonorId,
    resetPasswordFields,
    resetToken,
  ])

  const handleStartForgotPassword = useCallback(() => {
    if (flowStep !== 'password') {
      return
    }

    setAuthError(null)
    setAuthSuccess(null)
    setFlowStep('forgot-password')
  }, [flowStep])

  const handleBackToPasswordStep = useCallback(() => {
    setAuthError(null)
    setAuthSuccess(null)
    setFlowStep('password')
  }, [])

  const handlePrimaryAction = useCallback(() => {
    switch (flowStep) {
      case 'password':
        void handleLogin()
        break
      case 'register':
        void handleRegister()
        break
      case 'forgot-password':
        void handleForgotPassword()
        break
      case 'forgot-password-sent':
        handleBackToPasswordStep()
        break
      case 'reset-password':
        void handleResetPassword()
        break
      case 'phone':
      default:
        void handleCheckPhone()
        break
    }
  }, [
    flowStep,
    handleBackToPasswordStep,
    handleCheckPhone,
    handleForgotPassword,
    handleLogin,
    handleRegister,
    handleResetPassword,
  ])

  const campaign: LandingCampaign | null = landingData?.data?.campaign ?? null
  const institution: LandingInstitution | null = landingData?.data?.institution ?? null
  useLandingCampaignPageView(campaign?.id ?? null)

  const { openPurchaseWidget, isWidgetLoading, widgetError } =
    useLandingCampaignWidget(campaign)
  const salesOpen = isCampaignOpenForSales(campaign)
  const comprarDisabled = isLoading || !campaign?.id || isWidgetLoading || !salesOpen

  const showPurchaseWidget =
    Boolean(campaign) &&
    flowStep !== 'forgot-password' &&
    flowStep !== 'forgot-password-sent' &&
    flowStep !== 'reset-password'

  const primaryDisabled =
    isSsoPending ||
    isAutoAuthPending ||
    isSubmitting ||
    isLoading ||
    (Boolean(slug) && !institutionId && flowStep !== 'reset-password')

  useEffect(() => {
    initializeMetaPixel(campaign?.fb_pixel)
  }, [campaign?.fb_pixel])

  const campaignTitle = resolveLandingAccountTitle(campaign)
  const prizeLine = resolveLandingAccountPrizeLine(campaign)
  const backdropUrl = resolveLandingAccountBackdropUrl(campaign)
  const institutionName =
    buildLandingInstitutionName(institution) || campaignTitle || 'Sortee'
  const institutionBrandUrl = getInstitutionBrandImageUrl(institution)
  const homeHref = resolveCampaignHomeHref(
    slug,
    campaign?.custom_domain,
    searchParams.get('af')
  )

  const salesClosedNotice =
    campaign && !salesOpen ? (
      <LandingSalesClosedNotice campaign={campaign} visualVariant="light" />
    ) : null

  return (
    <div className={styles.page}>
      <ParticipantThemeStyle campaign={campaign} />

      <nav className={styles.mobilePageNav} aria-label="Navegação da campanha">
        <a href={homeHref} className={styles.backLink}>
          Início
        </a>
      </nav>

      <div className={styles.stage}>
        <aside className={styles.showcase} aria-label={campaignTitle || 'Campanha'}>
          {backdropUrl ? (
            <img
              src={backdropUrl}
              alt=""
              className={styles.showcasePhoto}
              decoding="async"
              fetchPriority="high"
            />
          ) : (
            <span className={styles.showcaseMark} aria-hidden>
              {institutionName.slice(0, 1)}
            </span>
          )}
          <div className={styles.showcaseVeil} />
          <div className={styles.showcaseCopy}>
            {institutionBrandUrl ? (
              <img src={institutionBrandUrl} alt="" className={styles.showcaseLogo} />
            ) : null}
            <p className={styles.showcaseKicker}>{institutionName}</p>
            <h1 className={styles.showcaseTitle}>
              {campaignTitle || 'Sua conta Sortee'}
            </h1>
            {prizeLine ? <p className={styles.showcaseExcerpt}>{prizeLine}</p> : null}
            <div className={styles.showcaseFeatures} aria-label="Recursos da sua conta">
              <span>
                <Icon icon="mdi:ticket-confirmation-outline" aria-hidden />{' '}
                {campaign?.generation_status === 'donation_only' ? 'Compras' : 'Números'}
              </span>
              <span>
                <Icon icon="mdi:star-four-points-outline" aria-hidden /> Conquistas
              </span>
              <span>
                <Icon icon="mdi:heart-outline" aria-hidden /> Participação
              </span>
            </div>
          </div>
        </aside>

        <main className={styles.board}>
          <div className={styles.boardInner}>
            <nav className={styles.boardNav} aria-label="Navegação da campanha">
              <a href={homeHref} className={styles.backLink}>
                Início
              </a>
            </nav>

            <p className={styles.boardEyebrow}>
              <Icon icon="mdi:shield-check-outline" aria-hidden /> Sua conta Sortee
            </p>

            {isAutoAuthPending || isSsoPending ? (
              <div className={styles.autoAuthPending} role="status">
                <Icon icon="mdi:loading" className={styles.autoAuthSpinner} aria-hidden />
                <p>Entrando na sua conta…</p>
              </div>
            ) : null}

            {isEmbeddedWindow ? (
              <section>
                <h1>Acesse sua conta Sortee</h1>
                <p>
                  Abra o acesso seguro para entrar na sua conta e continuar nesta
                  campanha.
                </p>
                <a
                  className={styles.submitButton}
                  href={window.location.href}
                  target="_top"
                >
                  Abrir acesso seguro
                </a>
              </section>
            ) : ssoConfirmation ? (
              <section aria-labelledby="sso-confirmation-title">
                <h1 id="sso-confirmation-title">Continuar com sua conta Sortee</h1>
                <p>Você está entrando na campanha {ssoConfirmation.campaign_name}.</p>
                <p>
                  Este endereço terá acesso à sua conta, aos seus dados e ao seu perfil:
                </p>
                <p>
                  <strong>{ssoConfirmation.redirect_origin}</strong>
                </p>
                <p>
                  Continue somente se reconhece este endereço e deseja usar sua conta
                  nele.
                </p>
                {authError ? <p role="alert">{authError}</p> : null}
                <button
                  type="button"
                  className={styles.submitButton}
                  disabled={isSsoPending}
                  onClick={() => void confirmSsoSignIn()}
                >
                  Continuar para a campanha
                </button>
                <button
                  type="button"
                  className={styles.textLinkButton}
                  disabled={isSsoPending}
                  onClick={cancelSsoSignIn}
                >
                  Cancelar
                </button>
              </section>
            ) : (
              <LandingAccountForm
                flowStep={flowStep}
                isLoading={isLoading || isAutoAuthPending || isSsoPending}
                isSubmitting={isSubmitting}
                authError={authError}
                authSuccess={authSuccess}
                widgetError={widgetError}
                phone={phone}
                loginPassword={loginPassword}
                accountPassword={accountPassword}
                accountPasswordConfirm={accountPasswordConfirm}
                registerName={registerName}
                registerCpf={registerCpf}
                registerEmail={registerEmail}
                resetEmail={resetEmail}
                salesOpen={salesOpen}
                showPurchaseWidget={showPurchaseWidget}
                primaryDisabled={primaryDisabled}
                comprarDisabled={comprarDisabled}
                isWidgetLoading={isWidgetLoading}
                salesClosedNotice={salesClosedNotice}
                onPhoneChange={setPhone}
                onLoginPasswordChange={setLoginPassword}
                onAccountPasswordChange={setAccountPassword}
                onAccountPasswordConfirmChange={setAccountPasswordConfirm}
                onRegisterNameChange={setRegisterName}
                onRegisterCpfChange={setRegisterCpf}
                onRegisterEmailChange={setRegisterEmail}
                onSubmit={handlePrimaryAction}
                onForgotPassword={handleStartForgotPassword}
                onBackToPassword={handleBackToPasswordStep}
                onBuy={openPurchaseWidget}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  )
}
